import { useMemo } from 'react';
import L from 'leaflet';
import { Marker as ReactMarker } from 'react-leaflet';
import {
  useAppSelector,
  activeImageOrDuplicateOfSelector,
  labelsSelector,
  effectiveLabelStyleSelector,
  DEFAULT_LABEL_STYLE,
  LabelStyle,
  LabelType,
} from '../../../../store';
import { LabelPreviewType } from './LabelEditing';
import {
  getLabelBadgeClassName,
  getLabelBadgeStyleVars,
  labelBadgeStyleVarsToString,
  labelScaleWithMap,
} from '../../shared/labelBadge';
import { labelHtml } from '../../../../utils/labelText';
import '../../shared/LabelBadge.scss';

const HIGHLIGHTED_Z_INDEX_OFFSET = 10000;

function labelIconHtml(
  name: string,
  resolvedStyle: ReturnType<ReturnType<typeof effectiveLabelStyleSelector>>,
  rotation: number,
  isSelected?: boolean,
  isHighlighted?: boolean,
  scaleZoom?: number,
  zoomOrigin?: { x: number; y: number },
): string {
  const nameHtml = labelHtml(name);
  // Inline bold only shows over normal text
  const hasInlineBold = /<(b|strong)>/.test(nameHtml);

  return `<div class="${getLabelBadgeClassName(resolvedStyle.variant, isSelected)}${hasInlineBold ? ' label-badge--inline-bold' : ''}${isHighlighted ? ' label-badge--highlighted' : ''}" style="${labelBadgeStyleVarsToString(getLabelBadgeStyleVars(
    // Unset values get their defaults there (e.g. the neon panel opacity)
    resolvedStyle,
    rotation,
    isSelected,
  ))}${scaleZoom === undefined ? '' : `;--label-scale:${labelScaleWithMap(scaleZoom)}`}${zoomOrigin ? `;--label-zoom-x:${zoomOrigin.x}px;--label-zoom-y:${zoomOrigin.y}px` : ''}"><span class="label-badge__text">${nameHtml}</span></div>`;
}

interface MarkerProps {
  label: LabelType;
  editedLabel?: LabelType | null;
  position: { x: number; y: number; rotation?: number; };
  handlers?: L.LeafletEventHandlerFnMap;
  isSelected?: boolean;
  /** Hovered, e.g. its overlay: shown above other labels, and zoomed in if scaled down with the map */
  isHighlighted?: boolean;
  /** Shown above all other labels, highlighted ones too (e.g. the edited one) */
  isOnTop?: boolean;
  draggable?: boolean;
  /** Style applied over the label's own style (e.g. from its overlay) */
  styleOverride?: Partial<LabelStyle>;
  /** Zoom showing the label at its size, scaling it with the map at other zooms */
  scaleZoom?: number;
  /** Point the hover zoom grows away from, in px from the label's top-left corner (default its center) */
  zoomOrigin?: { x: number; y: number };
  labelPreview?: LabelPreviewType | null;
  defaultRotation?: number;
};

export function Marker(props: MarkerProps) {
  const {
    label,
    editedLabel,
    position,
    handlers,
    isSelected,
    isHighlighted = false,
    isOnTop = false,
    draggable = false,
    styleOverride,
    scaleZoom,
    zoomOrigin,
    labelPreview,
    defaultRotation = 0,
  } = props;

  const activeImage = useAppSelector(activeImageOrDuplicateOfSelector);
  const placement = activeImage?.labels.find((lp) => lp.label === label.id);
  const labels = useAppSelector(labelsSelector);

  const getEffectiveStyle = useAppSelector(effectiveLabelStyleSelector);

  // Label not being edited
  let effectiveStyle = getEffectiveStyle(label);

  // Label is the main label being edited, and has a preview style
  if (editedLabel?.id === label.id && labelPreview) {
    // Changed target label in dropdown
    if (labelPreview.newLabelId !== label.id) {
      const newLabel = labels.find((l) => l.id === labelPreview.newLabelId);
      if (newLabel) {
        effectiveStyle = getEffectiveStyle(newLabel);
      }
    }
    effectiveStyle = { ...effectiveStyle, ...labelPreview.style };
  }

  // Label is selected (not the main label) and has a preview style
  if (editedLabel?.id !== label.id && isSelected && labelPreview) {
    effectiveStyle = { ...effectiveStyle, ...labelPreview.style };
  }

  if (styleOverride) {
    effectiveStyle = { ...effectiveStyle, ...styleOverride };
  }

  let rotation = position.rotation ?? defaultRotation;
  if (isSelected && labelPreview?.rotation !== undefined) {
    rotation = labelPreview.rotation;
  }

  const html = labelIconHtml(
    label.id === labelPreview?.labelId ? labelPreview.name : label.name,
    effectiveStyle,
    rotation,
    isSelected,
    isHighlighted,
    scaleZoom,
    zoomOrigin,
  );
  // Same icon while unchanged: replacing it re-creates the marker element, ending drags
  const icon = useMemo(() => L.divIcon({ html, className: 'label-div-icon', iconAnchor: [0, 0] }), [html]);

  if (!placement && label.id !== 'newLabel') return null;

  return (
    <ReactMarker
      key={label.id}
      position={[Number(position.x), Number(position.y)]}
      icon={icon}
      // Highlighted labels (maybe zoomed in) over the others, and labels on top over all
      zIndexOffset={(effectiveStyle.zIndex ?? DEFAULT_LABEL_STYLE.zIndex)
        + (isOnTop ? 2 * HIGHLIGHTED_Z_INDEX_OFFSET : isHighlighted ? HIGHLIGHTED_Z_INDEX_OFFSET : 0)}
      bubblingMouseEvents={false}
      draggable={draggable}
      eventHandlers={handlers}
    />
  );
}
